import React, { MouseEventHandler, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Connection, getConnectionHealthStatuses, Organization, ProviderType, DestinationType, Destination } from "../common/ObjectTypes";
import Pagination from "../common/Pagination";
import GlobalSearch from "../common/GlobalSearch";
import { containerClasses, textClasses, buttonClasses, tableClasses, filterClasses, paginationClasses } from "../common/tailwindClassnames";
import { Clear, CopyToClipboard, OneDirectionIcon, SortedDown, SortedUp, SortingArrows } from "../common/ProjectIcons";
import { toast } from 'react-toastify';
import NoResults from "../common/NoResults";
import { page_size } from "../../api/apiUtils";
import { Tooltip } from "../common/Tooltip";
import { getConnectionStatus } from "./getConnectionStatus";
import SelectInput from "../common/SelectInput";

type DestWithDisplay = Destination & { displayUrl: string, hasUrl: boolean };

const DestinationCellContent = React.memo(({ destinations, t }: {
    destinations: Destination[],
    t: (key: string, opts?: any) => string,
}) => {
    const groups = useMemo(() => {
        const withDisplay: DestWithDisplay[] = destinations.map((dest) => {
            if (!dest.base_url.trim()) return { ...dest, displayUrl: dest.name, hasUrl: false };
            let displayUrl = dest.base_url.trim();
            try { displayUrl = new URL(dest.base_url).hostname; } catch {}
            return { ...dest, displayUrl, hasUrl: true };
        });
        return withDisplay.reduce((acc: Record<string, DestWithDisplay[]>, dest) => {
            if (!acc[dest.type.name]) acc[dest.type.name] = [];
            acc[dest.type.name].push(dest);
            return acc;
        }, {});
    }, [destinations]);

    const MAX_VISIBLE = 2;
    return (
        <>
            {Object.entries(groups).map(([typeName, dests]) => {
                const visible = dests.slice(0, MAX_VISIBLE);
                const hiddenCount = dests.length - visible.length;
                return (
                    <div key={typeName} className="mb-1 last:mb-0">
                        <span className="block text-grey-g0 font-[510] leading-normal">{typeName}</span>
                        {visible.map((dest) => (
                            <div key={dest.id} className="flex items-center gap-1">
                                <span className="text-xs text-secondary-gray truncate" title={dest.hasUrl ? dest.base_url : dest.name}>
                                    {dest.displayUrl}
                                </span>
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        navigator.clipboard.writeText(dest.hasUrl ? dest.base_url : dest.name);
                                        toast.info(`${t("copied to clipboard")}`, {
                                            position: toast.POSITION.TOP_RIGHT,
                                            autoClose: 2000
                                        });
                                    }}
                                    className="flex-shrink-0 opacity-50 hover:opacity-100 transition-opacity"
                                    aria-label="Copy to clipboard"
                                >
                                    <CopyToClipboard />
                                </button>
                            </div>
                        ))}
                        {hiddenCount > 0 && (
                            <span
                                className="text-xs text-accent-green font-normal leading-normal underline"
                                style={{ textDecorationSkipInk: 'none', textUnderlinePosition: 'from-font' }}
                                title={dests.slice(MAX_VISIBLE).map(d => d.base_url || d.name).join(', ')}
                            >
                                {t("+ {{count}} other", { count: hiddenCount })}
                            </span>
                        )}
                    </div>
                );
            })}
        </>
    );
});
DestinationCellContent.displayName = 'DestinationCellContent';

const HealthLegend = ({ t }: { t: (key: string) => string }) => (
    <div className="text-xs text-left">
        <p className="font-semibold mb-1 text-off-black">{t("Connection Health")}</p>
        <p className="mb-1.5 text-secondary-gray">{t("Connections are analyzed every hour.")}</p>
        {(["healthy", "unhealthy", "disabled"] as const).map((status) => {
            const s = getConnectionStatus(status, false, t);
            return (
                <div key={status} className="flex items-center gap-1.5 mb-1">
                    <span className="flex-shrink-0">{s.icon}</span>
                    <span className="text-secondary-gray">{s.text}</span>
                </div>
            );
        })}
    </div>
);

type ConnectionsProps = {
    canAddConnection: boolean,
    connections: Connection[],
    reloadConnections: boolean,
    organizations: Organization[],
    destinationUrls: string[],
    providerTypes: ProviderType[],
    loadingProviderTypes: boolean,
    destinationTypes: DestinationType[],
    loadingDestinationTypes: boolean,
    onSelectConnection: Function,
    onChangeGlobalSearch: Function,
    onChangeStatusFilter: Function,
    statusFilterValue: any,
    onChangeDestinationUrl: Function,
    destinationFilterValue: any,
    loadingDestinationUrls: boolean,
    onChangeOwner: Function,
    ownerFilterValue: any,
    loadingOrganizations: boolean,
    onChangeProviderType: Function,
    providerTypeFilterValue: any,
    onChangeDestinationType: Function,
    destinationTypeFilterValue: any,
    globalSearchStr: string,
    requestSort: Function,
    onPageClicked: Function,
    totalCount: number,
    pageLoaded: boolean,
    take: number,
    offset: number,
    onClickAdd: MouseEventHandler
}

const ConnectionsList = ({
    canAddConnection,
    connections,
    reloadConnections,
    organizations,
    destinationUrls,
    providerTypes,
    loadingProviderTypes,
    destinationTypes,
    loadingDestinationTypes,
    onSelectConnection,
    onChangeGlobalSearch,
    onChangeStatusFilter,
    statusFilterValue,
    onChangeDestinationUrl,
    destinationFilterValue,
    loadingDestinationUrls,
    onChangeOwner,
    ownerFilterValue,
    loadingOrganizations,
    onChangeProviderType,
    providerTypeFilterValue,
    onChangeDestinationType,
    destinationTypeFilterValue,
    globalSearchStr,
    requestSort,
    onPageClicked,
    totalCount,
    pageLoaded,
    take,
    offset,
    onClickAdd
}: ConnectionsProps) => {
    const { t } = useTranslation();
    const [sortDirection, setSortDirection] = useState(true);
    const [sortByColumn, setSortByColumn] = useState("name");

    const connectionHealthStatuses = useMemo(() => getConnectionHealthStatuses(t), [t]);
    const destinationUrlOptions = useMemo(() => destinationUrls.map(url => {
        let hostname = url;
        try { hostname = new URL(url).hostname; } catch {}
        return { label: hostname, value: url };
    }), [destinationUrls]);

    return (
        <div className={containerClasses.page}>
            <div className="overflow-x-auto max-w-list-table"><div className="min-w-max">
            <div className="mb-12 flex justify-between">
                <div>
                    <h1 data-testid="connections_page_header" className={textClasses.heading}>
                        {t("Connections")}
                    </h1>
                </div>
                {canAddConnection && <button className={buttonClasses.primary}
                    onClick={onClickAdd}
                >
                    {t("Create Connection")}
                </button>}
            </div>
            {/* Horizontal filter bar */}
            <div className="flex flex-row gap-3 mb-3 flex-nowrap overflow-y-visible">
                {/* Search */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Search")}</span>
                    <GlobalSearch onChangeGlobalSearch={onChangeGlobalSearch} savedString={globalSearchStr} />
                </div>
                {/* Provider Type */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black capitalize">{t("source")}</span>
                    <SelectInput
                        name="provider_type__in"
                        options={providerTypes.map(p => ({ label: p.name, value: p.value }))}
                        value={providerTypeFilterValue}
                        onChange={onChangeProviderType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingProviderTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination Type */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Destination")}</span>
                    <SelectInput
                        name="destination_type__in"
                        options={destinationTypes.map(d => ({ label: d.name, value: d.value }))}
                        value={destinationTypeFilterValue}
                        onChange={onChangeDestinationType}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationTypes}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Destination */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Destination URL")}</span>
                    <SelectInput
                        name="destination_url__in"
                        options={destinationUrlOptions}
                        value={destinationFilterValue}
                        onChange={onChangeDestinationUrl}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingDestinationUrls}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* Workspace */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Workspace")}</span>
                    <SelectInput
                        name="owner__in"
                        options={organizations.map(org => ({ label: org.name, value: org.id }))}
                        value={ownerFilterValue}
                        onChange={onChangeOwner}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={true}
                        isDisabled={false}
                        isLoading={loadingOrganizations}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
                {/* State */}
                <div className="flex flex-col gap-1">
                    <span className="text-sm font-semibold text-off-black">{t("Health")}</span>
                    <SelectInput
                        name="status"
                        options={connectionHealthStatuses.map(s => ({ label: s.name, value: s.value }))}
                        value={statusFilterValue}
                        onChange={onChangeStatusFilter}
                        isMulti={false}
                        isClearable={true}
                        isSearchable={false}
                        isDisabled={false}
                        isLoading={false}
                        className="w-48"
                        variant="connectionFilter"
                    />
                </div>
            </div>
            {/* Filter chips */}
            {(globalSearchStr || statusFilterValue || destinationFilterValue || ownerFilterValue || providerTypeFilterValue || destinationTypeFilterValue) &&
                <div className="flex flex-row flex-wrap gap-2 mb-3">
                    {globalSearchStr && <span className={filterClasses.tag}>
                        {globalSearchStr}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeGlobalSearch('')}><Clear /></span>
                    </span>}
                    {statusFilterValue && <span className={filterClasses.tag}>
                        {(statusFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeStatusFilter(null)}><Clear /></span>
                    </span>}
                    {destinationFilterValue && <span className={filterClasses.tag}>
                        {(destinationFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeDestinationUrl(null)}><Clear /></span>
                    </span>}
                    {ownerFilterValue && <span className={filterClasses.tag}>
                        {(ownerFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeOwner(null)}><Clear /></span>
                    </span>}
                    {providerTypeFilterValue && <span className={filterClasses.tag}>
                        {(providerTypeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeProviderType(null)}><Clear /></span>
                    </span>}
                    {destinationTypeFilterValue && <span className={filterClasses.tag}>
                        {(destinationTypeFilterValue as any).label}
                        <span className="cursor-pointer text-dark-green" onClick={() => onChangeDestinationType(null)}><Clear /></span>
                    </span>}
                </div>
            }
            {/* Table */}
            <div className="flex flex-col w-full">
                    <table className={tableClasses.container}>
                        <thead className={tableClasses.header}>
                            <tr>
                                <th className={tableClasses.headerCell} style={{ width: "350px", maxWidth: "350px" }}>
                                    {t("Name")}
                                    <span className={tableClasses.sortIcon} onClick={() => {
                                        requestSort("name", sortDirection)
                                        setSortDirection(!sortDirection)
                                        setSortByColumn("name")
                                    }}>
                                        {sortByColumn === "name" ?
                                            sortDirection ? <SortedUp /> : <SortedDown />
                                            : <SortingArrows />}
                                    </span>
                                </th>
                                <th className={tableClasses.headerCell} style={{ width: "160px", maxWidth: "160px" }}>
                                    <span className="sr-only capitalize">{t("source")}</span>
                                </th>
                                <th className={tableClasses.headerCell} style={{ width: "52px", minWidth: "52px", paddingLeft: "20px", paddingRight: "20px" }}>
                                    <span className="sr-only">{t("Direction")}</span>
                                </th>
                                <th className={tableClasses.headerCell} style={{ width: "320px", maxWidth: "320px" }}>
                                    <span className="sr-only">{t("Destination")}</span>
                                </th>
                                <th className={tableClasses.headerCell}>
                                    {t("Workspace")}
                                    <span className={tableClasses.sortIcon} onClick={() => {
                                        requestSort("owner__name", sortDirection)
                                        setSortDirection(!sortDirection)
                                        setSortByColumn("owner__name")
                                    }}>
                                        {sortByColumn === "owner__name" ?
                                            sortDirection ? <SortedUp /> : <SortedDown />
                                            : <SortingArrows />}
                                    </span>
                                </th>
                                <th className={`${tableClasses.headerCell} flex flex-row align-top`} style={{ width: "110px", minWidth: "110px" }}>
                                    {t("Health")}
                                    <span className="ml-1 cursor-pointer font-normal normal-case -mb-1.5">
                                        <Tooltip
                                            position="bottomleft"
                                            width="w-52"
                                            ariaLabel={t("Health status legend")}
                                            description={<HealthLegend t={t} />}
                                        />
                                    </span>
                                </th>
                            </tr>
                        </thead>
                        {(!pageLoaded || reloadConnections) ?
                            <tbody className="text-sm">
                                {[...Array(8)].map((_, i) => (
                                    <tr key={i} className={tableClasses.row} data-testid="loading">
                                        {/* Name */}
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-48 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        {/* Source tech: type name + URL below */}
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-24 bg-neutral-gray-50 rounded animate-pulse mb-1" />
                                            <div className="h-3 w-32 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        {/* Arrow icon */}
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-4 bg-neutral-gray-50 rounded animate-pulse mx-auto" />
                                        </td>
                                        {/* Destination: type name + URL below */}
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-24 bg-neutral-gray-50 rounded animate-pulse mb-1" />
                                            <div className="h-3 w-32 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        {/* Workspace */}
                                        <td className={tableClasses.cell}>
                                            <div className="h-4 w-24 bg-neutral-gray-50 rounded animate-pulse" />
                                        </td>
                                        {/* Health icon */}
                                        <td className={tableClasses.cell} style={{ width: "110px", minWidth: "110px" }}>
                                            <div className="h-5 w-5 bg-neutral-gray-50 rounded animate-pulse ml-2" />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                                :
                                <tbody className="text-sm cursor-pointer">
                                    {connections.map((connection: Connection) => {
                                        const connectionStatus = getConnectionStatus(connection.status, false, t);
                                        const navigateToConnection = (e: React.MouseEvent) => {
                                            if (e.metaKey || e.ctrlKey) {
                                                window.open(`/connections/${connection.id}`, '_blank', 'noopener,noreferrer');
                                            } else {
                                                onSelectConnection(connection.id);
                                            }
                                        };
                                        return (
                                            <tr key={connection.id} className={tableClasses.row}>
                                                {/* Connection name */}
                                                <td className={tableClasses.cell} style={{ width: "350px", maxWidth: "350px" }} onClick={navigateToConnection}>
                                                    <span className="block truncate" title={connection.provider.name}>
                                                        {connection.provider.name}
                                                    </span>
                                                </td>
                                                {/* Source technology */}
                                                <td className={tableClasses.cell} style={{ width: "160px", maxWidth: "160px" }} onClick={navigateToConnection}>
                                                    <span className="block text-grey-g0 font-[510] leading-normal">{connection.provider.type.name}</span>
                                                    {connection.provider.base_url && (
                                                        <span className="block text-xs text-secondary-gray truncate" title={connection.provider.base_url}>
                                                            {(() => { try { return new URL(connection.provider.base_url).hostname; } catch { return connection.provider.base_url; } })()}
                                                        </span>
                                                    )}
                                                </td>
                                                {/* Direction arrow */}
                                                <td className={tableClasses.cell} style={{ width: "52px", minWidth: "52px", paddingLeft: "20px", paddingRight: "20px" }} onClick={navigateToConnection}>
                                                    <OneDirectionIcon />
                                                </td>
                                                {/* Destination technology */}
                                                <td className={tableClasses.cell} style={{ width: "320px", maxWidth: "320px" }} onClick={navigateToConnection}>
                                                    {connection.destinations.length === 0 && (
                                                        <span className="text-sm text-off-black">{t("Destination not set")}</span>
                                                    )}
                                                    <DestinationCellContent destinations={connection.destinations} t={t} />
                                                </td>
                                                {/* Workspace */}
                                                <td className={tableClasses.cell} onClick={navigateToConnection}>
                                                    {connection.owner.name}
                                                </td>
                                                {/* Health */}
                                                <td className={tableClasses.cell} style={{ width: "110px", minWidth: "110px" }} onClick={navigateToConnection}>
                                                    <span title={connectionStatus.text} aria-label={connectionStatus.text} className="flex items-center pl-2">
                                                        {connectionStatus.icon}
                                                    </span>
                                                </td>
                                            </tr>
                                        )
                                    })}
                                </tbody>}
                        {!pageLoaded || reloadConnections ? null : connections.length === 0 &&
                            <tbody><tr><td colSpan={6} className="py-16">
                                <NoResults
                                    headerMsg={t("No Results Found")}
                                    labelMsg={t("You may need to adjust your search or filters to find what you are looking for")}
                                />
                            </td></tr></tbody>}
                        </table>
                    {totalCount > page_size && <Pagination
                        showText={true}
                        handlePageClick={onPageClicked}
                        pageCount={Math.ceil(totalCount / take)}
                        resultStart={offset + 1}
                        resultEnd={(offset + take) < totalCount ? (offset + take) : totalCount}
                        totalCount={totalCount}
                        initialPage={Math.floor(offset / take)}
                        take={take}
                        loading={!pageLoaded}
                        mainclassname={paginationClasses.container}
                    />}
            </div>
            </div></div>
        </div>
    )
};

export default ConnectionsList;
